/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

/**
 * Centralized Markdown parsing utility with lazy loading
 * Optimizes bundle size by loading marked library only when needed
 */

type MarkdownParser = typeof import('marked');

let markedInstance: MarkdownParser | null = null;

/**
 * Lazy load the marked library and parse markdown to HTML
 * @param content - Markdown content to parse
 * @param options - Optional marked parse options
 * @returns Promise resolving to HTML string
 */
export async function parseMarkdown(content: string, options?: import('marked').MarkedOptions): Promise<string> {
    if (!markedInstance) {
        // Dynamic import to optimize initial bundle size
        markedInstance = await import('marked');
        
        // Configure marked options for better link rendering
        markedInstance.use({
            renderer: {
                link(this: any, { href, title, text }: import('marked').Tokens.Link): string {
                    const titleAttr = title ? ` title="${title}"` : '';
                    return `<a href="${href}"${titleAttr} target="_blank" rel="noopener noreferrer" class="markdown-link">${text}</a>`;
                }
            }
        });
    }
    
    return markedInstance.parse(content, options);
}

/**
 * Synchronous markdown parsing (only use if marked is already loaded)
 * @param content - Markdown content to parse
 * @param options - Optional marked parse options
 * @returns HTML string or throws error if not loaded
 */
export function parseMarkdownSync(content: string, options?: import('marked').MarkedOptions): string {
    if (!markedInstance) {
        throw new Error('Markdown parser not loaded. Use parseMarkdown() for async parsing first.');
    }
    
    return markedInstance.parse(content, options) as string;
}

/**
 * Preload the marked library for better performance on subsequent calls
 */
export async function preloadMarkdownParser(): Promise<void> {
    if (!markedInstance) {
        markedInstance = await import('marked');
    }
}
